import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
  useHumanDecisionSignals,
  useReviewCases,
  useReviewHumanDecisionSignal,
  useReviewThinkingLabelProposal,
  type HumanDecisionRejectReason,
  type HumanDecisionSignal,
  type ReviewCase,
} from "../../queries/useAgentLearning";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  buildCorrectionPayload,
  buildRejectPayload,
  compactSignalText,
  formatSignalDate,
  groupSignalsByType,
  rejectReasonDescriptions,
  rejectReasonLabels,
} from "./dailyReviewUtils";

const rejectReasons = Object.keys(rejectReasonLabels) as HumanDecisionRejectReason[];
const helpPanelStorageKey = "murray-daily-review-help-open";

export function DailyReviewContent() {
  const signals = useHumanDecisionSignals("pending_review");
  const reviewCases = useReviewCases();
  const review = useReviewHumanDecisionSignal();
  const reviewThinking = useReviewThinkingLabelProposal();
  const groups = groupSignalsByType(signals.data?.items || []);
  const hiddenThinkingProposalKeys = new Set(
    (signals.data?.items || [])
      .filter((signal) => signal.source_table === "vonigo_label_proposals" && signal.source_pk)
      .map((signal) => `vonigo_label_proposals:${signal.source_pk}`),
  );
  const thinkingProposals = (reviewCases.data?.items || [])
    .filter((item) => (
      item.case_type === "label_proposal"
      && item.proposed_semantic === "thinking_about_it"
      && item.queue_status === "needs_marc"
      && !hiddenThinkingProposalKeys.has(`vonigo_label_proposals:${item.case_ref}`)
    ));
  const isLoading = signals.isLoading || reviewCases.isLoading;
  const pendingCount = (signals.data?.summary.total || 0) + thinkingProposals.length;
  const [corrections, setCorrections] = useState<Record<number, string>>({});
  const [validationNotes, setValidationNotes] = useState<Record<number, string>>({});
  const [rejectDrafts, setRejectDrafts] = useState<Record<number, HumanDecisionRejectReason>>({});
  const [helpOpen, setHelpOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.localStorage.getItem(helpPanelStorageKey);
    return saved === null ? true : saved === "true";
  });

  const updateCorrection = (id: number, value: string) => {
    setCorrections((current) => ({ ...current, [id]: value }));
  };

  const updateValidationNote = (id: number, value: string) => {
    setValidationNotes((current) => ({ ...current, [id]: value }));
  };

  const updateRejectReason = (id: number, value: HumanDecisionRejectReason) => {
    setRejectDrafts((current) => ({ ...current, [id]: value }));
  };

  const validateSignal = (signal: HumanDecisionSignal, note?: string) => {
    const trimmedNote = note?.trim();
    review.mutate({ id: signal.id, action: "validate", note: trimmedNote || undefined });
  };

  const validateSignalWithNote = (signal: HumanDecisionSignal) => {
    validateSignal(signal, validationNotes[signal.id]);
  };

  const correctSignal = (signal: HumanDecisionSignal) => {
    const payload = buildCorrectionPayload(corrections[signal.id] || "");
    if (!payload.correction.human_decision) return;
    review.mutate({
      id: signal.id,
      action: "correct",
      correction: payload.correction,
      note: "Corrected from Daily review.",
    });
  };

  const rejectSignal = (signal: HumanDecisionSignal) => {
    const payload = buildRejectPayload(rejectDrafts[signal.id] || "checker_noise");
    review.mutate({
      id: signal.id,
      action: "reject",
      reason: payload.reason,
      note: "Rejected from Daily review.",
    });
  };

  const validateThinkingProposal = (proposal: ReviewCase) => {
    reviewThinking.mutate({
      id: proposal.case_ref,
      action: "validate",
      note: "Validated Thinking label proposal from Daily review.",
    });
  };

  const rejectThinkingProposal = (proposal: ReviewCase) => {
    reviewThinking.mutate({
      id: proposal.case_ref,
      action: "reject",
      note: "Canceled Thinking label proposal from Daily review.",
    });
  };

  const toggleHelp = () => {
    setHelpOpen((current) => {
      const next = !current;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(helpPanelStorageKey, String(next));
      }
      return next;
    });
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <Badge variant="slate">Agent learning</Badge>
          <h1 className="mt-3 text-2xl font-semibold tracking-normal text-slate-950 md:text-3xl">
            Daily review
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Review captured human decisions before they become graduation-eligible learning examples.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="green">Learning only</Badge>
          <Badge variant="green">No customer impact</Badge>
        </div>
      </div>

      <section className="rounded-md border border-sky-100 bg-sky-50 p-4 text-slate-900" data-testid="daily-review-help-panel">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-950">How this page works</h2>
            {helpOpen ? (
              <div id="daily-review-help-body" className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <p>Review each captured decision and teach Murray.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Validate: the decision shown is correct. It becomes training truth.</li>
                  <li>Correct: the decision was wrong. Enter the right one.</li>
                  <li>Reject: not a teaching case. Pick a reason below.</li>
                </ul>
                <p>This page never sends messages or edits templates. Validating Thinking About It can write Vonigo only when its dedicated live flag is enabled.</p>
                <p>Only Validated and Corrected decisions feed Murray&apos;s memory.</p>
              </div>
            ) : null}
          </div>
          <Button
            aria-expanded={helpOpen}
            aria-controls="daily-review-help-body"
            data-testid="daily-review-help-toggle"
            onClick={toggleHelp}
            variant="secondary"
          >
            {helpOpen ? "Hide" : "Show"}
          </Button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <ReviewStat label="Pending" value={isLoading ? "-" : String(pendingCount)} />
        <ReviewStat label="Guardrail" value={signals.data?.guardrails.can_send_messages ? "Check" : "No sends"} />
        <ReviewStat label="Promotion" value="Manual only" />
      </section>

      {isLoading ? (
        <StateCard title="Loading captured decisions" body="Murray is loading the pending review queue." />
      ) : null}

      {!isLoading && signals.isError ? (
        <StateCard title="Daily review is unavailable" body="The agent learning endpoint could not load pending signals." tone="red" />
      ) : null}

      {!isLoading && reviewCases.isError ? (
        <StateCard title="Thinking proposals are unavailable" body="The label proposal queue could not load right now." tone="red" />
      ) : null}

      {!isLoading && !signals.isError && !reviewCases.isError && groups.length === 0 && thinkingProposals.length === 0 ? (
        <StateCard title="No pending decisions" body="Nothing is waiting for review right now." tone="green" />
      ) : null}

      {!isLoading && !reviewCases.isError && thinkingProposals.length > 0 ? (
        <section className="rounded-md border border-amber-200 bg-white" data-testid="daily-review-thinking-proposals">
          <header className="border-b border-amber-100 bg-amber-50 px-4 py-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-950">Thinking label proposals</h2>
              </div>
              <Badge variant="amber">{thinkingProposals.length} pending</Badge>
            </div>
          </header>
          <div className="space-y-3 p-4">
            {thinkingProposals.map((proposal) => (
              <ThinkingProposalRow
                key={proposal.case_ref}
                disabled={reviewThinking.isPending}
                proposal={proposal}
                onReject={() => rejectThinkingProposal(proposal)}
                onValidate={() => validateThinkingProposal(proposal)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {!isLoading && !signals.isError && groups.length > 0 ? (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.type} className="rounded-md border border-slate-200 bg-white">
              <header className="border-b border-slate-100 px-4 py-3">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <h2 className="text-base font-semibold text-slate-950">{group.label}</h2>
                  <Badge variant="slate">{group.items.length} pending</Badge>
                </div>
              </header>
              <div className="space-y-3 p-4">
                {group.items.map((signal) => (
                  <SignalReviewRow
                    key={signal.id}
                    correction={corrections[signal.id] || ""}
                    disabled={review.isPending}
                    rejectReason={rejectDrafts[signal.id] || "checker_noise"}
                    signal={signal}
                    validationNote={validationNotes[signal.id] || ""}
                    onCorrect={() => correctSignal(signal)}
                    onCorrectionChange={(value) => updateCorrection(signal.id, value)}
                    onReject={() => rejectSignal(signal)}
                    onRejectReasonChange={(value) => updateRejectReason(signal.id, value)}
                    onValidate={() => validateSignal(signal)}
                    onValidateWithNote={() => validateSignalWithNote(signal)}
                    onValidationNoteChange={(value) => updateValidationNote(signal.id, value)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}

      {review.isError || reviewThinking.isError ? (
        <p className="rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800">
          The review action could not be saved.
        </p>
      ) : null}
    </section>
  );
}

function ThinkingProposalRow({
  disabled,
  proposal,
  onReject,
  onValidate,
}: {
  disabled: boolean;
  proposal: ReviewCase;
  onReject: () => void;
  onValidate: () => void;
}) {
  const title = proposal.customer_name || `Quote #${proposal.quote_id || "unknown"}`;
  return (
    <article
      className="rounded-md border border-amber-100 bg-white p-4"
      data-testid={`daily-review-thinking-proposal-${proposal.quote_id || proposal.case_ref}`}
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="amber">Thinking About It</Badge>
            {proposal.quote_id ? <Badge variant="slate">Quote #{proposal.quote_id}</Badge> : null}
            {proposal.channel ? <Badge variant="slate">{proposal.channel}</Badge> : null}
            {proposal.proposal_date ? <span className="text-xs text-slate-500">{formatSignalDate(proposal.proposal_date)}</span> : null}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
            <p className="mt-1 text-sm text-slate-700">
              {compactSignalText(proposal.customer_message_excerpt || proposal.evidence_snippet || "")}
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <SignalText label="Proposed label" value={proposal.proposed_label} />
            <SignalText label="Current Vonigo label" value={proposal.current_vonigo_raw_label || proposal.current_vonigo_status} />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid={`daily-review-thinking-validate-${proposal.case_ref}`}
              disabled={disabled}
              onClick={onValidate}
            >
              Validate
            </Button>
            <Button
              data-testid={`daily-review-thinking-cancel-${proposal.case_ref}`}
              disabled={disabled}
              onClick={onReject}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
          {proposal.quote_id ? (
            <Link to="/quotes/$quoteId" params={{ quoteId: proposal.quote_id }}>
              <Button variant="secondary">Open quote</Button>
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function SignalReviewRow({
  correction,
  disabled,
  rejectReason,
  signal,
  validationNote,
  onCorrect,
  onCorrectionChange,
  onReject,
  onRejectReasonChange,
  onValidate,
  onValidateWithNote,
  onValidationNoteChange,
}: {
  correction: string;
  disabled: boolean;
  rejectReason: HumanDecisionRejectReason;
  signal: HumanDecisionSignal;
  validationNote: string;
  onCorrect: () => void;
  onCorrectionChange: (value: string) => void;
  onReject: () => void;
  onRejectReasonChange: (value: HumanDecisionRejectReason) => void;
  onValidate: () => void;
  onValidateWithNote: () => void;
  onValidationNoteChange: (value: string) => void;
}) {
  const correctionReady = correction.trim().length > 0;
  const validationNoteReady = validationNote.trim().length > 0;
  return (
    <article className="rounded-md border border-slate-200 bg-white p-4" data-testid={`daily-review-signal-${signal.id}`}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="slate">Quote #{signal.quote_id}</Badge>
            <Badge variant="slate">{signal.channel}</Badge>
            <span className="text-xs text-slate-500">{formatSignalDate(signal.captured_at)}</span>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <SignalText label="Agent proposed" value={signal.agent_proposal} />
            <SignalText label="Human decision" value={signal.human_decision} />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>Type: {signal.decision_type}</span>
            {signal.disposition_type ? <span>Disposition: {signal.disposition_type}</span> : null}
            <span>Captured by {signal.captured_by}</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button data-testid={`daily-review-validate-${signal.id}`} disabled={disabled} onClick={onValidate}>
              Validate
            </Button>
            <Link to="/quotes/$quoteId" params={{ quoteId: signal.quote_id }}>
              <Button variant="secondary">Open quote</Button>
            </Link>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500" htmlFor={`validation-note-${signal.id}`}>
              Validation note
            </label>
            <Input
              data-testid={`daily-review-validation-note-${signal.id}`}
              id={`validation-note-${signal.id}`}
              placeholder="Add context or next step"
              value={validationNote}
              onChange={(event) => onValidationNoteChange(event.target.value)}
            />
            <Button
              data-testid={`daily-review-validate-note-${signal.id}`}
              disabled={disabled || !validationNoteReady}
              onClick={onValidateWithNote}
              variant="secondary"
            >
              Validate + note
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500" htmlFor={`correction-${signal.id}`}>
              Correction
            </label>
            <Input
              data-testid={`daily-review-correction-${signal.id}`}
              id={`correction-${signal.id}`}
              placeholder="Enter the corrected decision"
              value={correction}
              onChange={(event) => onCorrectionChange(event.target.value)}
            />
            <Button data-testid={`daily-review-correct-${signal.id}`} disabled={disabled || !correctionReady} onClick={onCorrect} variant="secondary">
              Correct
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500" htmlFor={`reject-${signal.id}`}>
              Reject reason
            </label>
            <select
              data-testid={`daily-review-reject-reason-${signal.id}`}
              id={`reject-${signal.id}`}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              value={rejectReason}
              onChange={(event) => onRejectReasonChange(event.target.value as HumanDecisionRejectReason)}
            >
              {rejectReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {rejectReasonLabels[reason]}
                </option>
              ))}
            </select>
            <p className="text-xs leading-5 text-slate-500" data-testid={`daily-review-reject-help-${signal.id}`}>
              {rejectReasonDescriptions[rejectReason]}
            </p>
            <Button data-testid={`daily-review-reject-${signal.id}`} disabled={disabled} onClick={onReject} variant="ghost">
              Reject
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

function SignalText({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <p className="mt-1 text-sm text-slate-800">{compactSignalText(value)}</p>
    </div>
  );
}

function ReviewStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function StateCard({ title, body, tone = "slate" }: { title: string; body: string; tone?: "slate" | "green" | "red" }) {
  const toneClass = tone === "green"
    ? "border-emerald-100 bg-emerald-50 text-emerald-900"
    : tone === "red"
      ? "border-red-100 bg-red-50 text-red-900"
      : "border-slate-200 bg-white text-slate-700";
  return (
    <div className={`rounded-md border p-5 ${toneClass}`}>
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-sm opacity-80">{body}</p>
    </div>
  );
}
